import { extractAdminTokens, getStoredAdminSession } from "./admin-login-api";

export interface AdminUser {
  id: string;
  username: string;
  full_name: string;
  email: string;
  phone: string;
  is_active: boolean;
  account_type: "devotee" | "volunteer";
  volunteer_id: number | null;
  volunteer_active: boolean | null;
  volunteer_is_deactivated: boolean | null;
  created_at: string;
}

export interface UserFilters {
  account_type: "all" | AdminUser["account_type"];
  is_active: "all" | "true" | "false";
  search: string;
  page: number;
  page_size: number;
}

export interface UserPage {
  count: number;
  page: number;
  page_size: number;
  results: AdminUser[];
}

export class AdminUserError extends Error {
  constructor(message: string, public code?: string) {
    super(message);
  }
}

async function request<T>(suffix: string, init: RequestInit = {}): Promise<T> {
  const base = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "");
  if (!base) { throw new Error("API base URL not configured (VITE_API_BASE_URL)."); }
  const { access } = extractAdminTokens(getStoredAdminSession()?.session);
  if (!access) { throw new Error("Please sign in with an admin account."); }
  const tenant = import.meta.env.VITE_X_TENANTID as string | undefined;
  const response = await fetch(`${base}/jain_gaurav_yatra/admin/users/${suffix}`, {
    ...init,
    credentials: "include",
    headers: {
      Authorization: `Bearer ${access}`,
      ...(tenant ? { "X-Tenant-ID": tenant } : {}),
      ...(init.body ? { "Content-Type": "application/json" } : {}),
    },
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new AdminUserError(
      data?.detail || data?.message || (response.status === 403
        ? "Only admin accounts can manage users."
        : `Request failed (${response.status}).`),
      data?.code,
    );
  }
  if (!data) { throw new Error("The server returned an empty response."); }
  return data as T;
}

export function listAdminUsers(filters: UserFilters, signal?: AbortSignal) {
  const query = new URLSearchParams({
    account_type: filters.account_type,
    is_active: filters.is_active,
    search: filters.search,
    page: String(filters.page),
    page_size: String(Math.min(100, Math.max(1, filters.page_size))),
  });
  return request<UserPage>(`?${query}`, { signal });
}

export function updateAdminUser(id: string, profile: Pick<AdminUser, "full_name" | "phone">) {
  return request<AdminUser>(`${encodeURIComponent(id)}/`, {
    method: "PATCH",
    body: JSON.stringify({ full_name: profile.full_name, phone: profile.phone }),
  });
}

export function setAdminUserActive(id: string, active: boolean) {
  return request<{ detail: string; code: string; user: AdminUser }>(
    `${encodeURIComponent(id)}/${active ? "activate" : "deactivate"}/`,
    { method: "POST" },
  );
}
